import type { DataTableColumnDef, DataTableRowData } from '@neo/blocks/cx-data-table'
import type { DataTableFilterField } from '@neo/blocks/data-table/types'
import { cn } from '@neo/lib/utils'
import { Badge } from '@neo/primitives/badge'
import { StatusIndicator } from '@neo/primitives/status-indicator'
import { Tooltip } from '@neo/primitives/tooltip'
import { TruncatedTextWithTooltip } from '@neo/primitives/truncated-text-with-tooltip'
import { capitalize } from 'lodash'
import { AlertTriangle, ChevronDown, ChevronUp, FileText, Globe, RefreshCcw } from 'lucide-react'
import { DATE_TIME_FORMAT, formatDateTime } from 'v2/api-console/shared/utils/date-utils'
import { getAppDetails } from 'v2/pages/flow/utils'
import { getAccountIdentifier } from 'v2/pages/settings/integrations/utils/account-identifier'
import type { RExternalApp, RExternalOrgApp } from 'v2/types/api/settings'
import { CountBadges } from './count-badges'
import { KBActions } from './kb-actions'
import { type DocumentStatus, KBDocumentSource, type RKnowledgeBase } from './types'

export interface KBRow extends DataTableRowData {
  [key: string]: unknown
  id: string
  name: string
  display_name?: string
  status: DocumentStatus
  source: KBDocumentSource
  last_updated_on: string
  isSource: boolean
  url: string
  subRows?: KBRow[]
  // TRUE totals for a source row (subRows is the capped top-N, errored-first display list). Drive the
  // "N Documents" badge + the "showing first M" / "K failed" / "K empty" affordances from these, not
  // subRows.length.
  document_count?: number
  error_count?: number
  empty_count?: number
  embedding_status: DocumentStatus
  enabled: boolean
  updated_by: { uuid: string; name: string }
  updated_at: string
  parentKnowledgeBaseId: string
  fetching: boolean
  app_source: string
  app_source_id?: string
  document_name?: string
  error_detail?: string | null
  // chunks embedded for this doc (leaf rows only). 0 on a "completed" doc means it produced no
  // usable content — surfaced as a "No content" failure instead of a false "Active".
  chunk_count?: number
  // The page genuinely had no content (benign) — render a muted "Empty", NOT the red "No content".
  is_empty?: boolean
  download_url?: string | null
}

const getAppIcon = (appSource: string, apps: RExternalApp) => {
  const appDetails = getAppDetails(appSource, apps)
  if (!appDetails) return null
  return <img src={appDetails.logo_url} alt={appSource} width={18} height={18} />
}

export const getColumns = (
  apps: RExternalApp,
  connectedOrgApps?: RExternalOrgApp[],
): DataTableColumnDef<KBRow>[] => [
  {
    id: 'expander',
    header: '',
    Cell: ({ row }) =>
      row.getCanExpand() ? (
        <button
          onClick={() => row.toggleExpanded?.()}
          data-testid="expander"
          className="mr-2"
          aria-label={row.getIsExpanded() ? 'Collapse row' : 'Expand row'}
          type="button"
        >
          {row.getIsExpanded() ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      ) : null,
    size: 32,
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'name',
    header: 'Name',
    size: 420,
    Cell: ({ row }) => {
      const { isSource, source, app_source: appSource } = row.original

      const renderUrlSource = () => (
        <>
          <div className="flex items-center gap-2">
            {row.original.url && (
              <Tooltip content={row.original.url}>
                <span className="font-jetbrains text-xs-500 truncate">{row.original.url}</span>
              </Tooltip>
            )}
          </div>
          {/* Count badges (true total + "showing first M" + "K failed") for the URL source */}
          {source === KBDocumentSource.URL && <CountBadges noun="URL" original={row.original} />}
        </>
      )

      const renderAppSource = () => {
        const appDetails = getAppDetails(appSource, apps)
        if (!appDetails) return null

        // Find the specific connected app using app_source_id
        const connectedApp = connectedOrgApps?.find((app) =>
          row.original.app_source_id
            ? app.id === row.original.app_source_id
            : app.provider === appSource && app.state === 'ACTIVE',
        )
        const accountIdentifier = connectedApp ? getAccountIdentifier(connectedApp) : undefined

        return (
          <>
            <div className="flex items-center gap-2">
              <TruncatedTextWithTooltip>
                <span className="text-s-500 truncate">
                  {row.original.display_name || capitalize(appSource)}
                  {accountIdentifier && (
                    <span className="text-xs-400 text-muted-foreground ml-1">
                      ({accountIdentifier})
                    </span>
                  )}
                </span>
              </TruncatedTextWithTooltip>
            </div>
            {/* Count badges (true total + "showing first M" + "K failed") for the App source */}
            <CountBadges noun="Document" original={row.original} />
          </>
        )
      }

      const renderFileSource = () => (
        <div className="flex items-center gap-2">
          <TruncatedTextWithTooltip>
            <span className="text-s-500 truncate">
              {row.original.display_name || row.original.name}
            </span>
          </TruncatedTextWithTooltip>
        </div>
      )

      const renderChildDocument = () => (
        <>
          {source === KBDocumentSource.URL && <FileText size={16} className="text-secondary" />}
          {source === KBDocumentSource.APP && getAppIcon(appSource, apps)}
          {source === KBDocumentSource.FILE && <FileText size={16} className="text-secondary" />}
          <TruncatedTextWithTooltip>{row.original.display_name}</TruncatedTextWithTooltip>
        </>
      )

      const getContent = () => {
        if (source === KBDocumentSource.URL && isSource) return renderUrlSource()
        if (source === KBDocumentSource.APP && isSource) return renderAppSource()
        if (source === KBDocumentSource.FILE && isSource) return renderFileSource()
        return renderChildDocument()
      }

      return <div className="flex items-center gap-2 truncate">{getContent()}</div>
    },
  },
  {
    id: 'status',
    header: 'Status',
    Cell: ({ row }) => {
      const status = row.original.isSource ? row.original.status : row.original.embedding_status
      const isDisabled = row.original.isSource ? false : !row.original.enabled
      const { isSource, fetching } = row.original

      if (isDisabled) return <StatusIndicator tone="disabled">Disabled</StatusIndicator>
      if (isSource && fetching) {
        const loadingText =
          row.original.source === KBDocumentSource.URL ? 'Crawling...' : 'Pending...'
        return <StatusIndicator tone="progress">{loadingText}</StatusIndicator>
      }

      if (status === 'completed_with_errors')
        return (
          <div className="flex items-center gap-2">
            <StatusIndicator tone="success">Active</StatusIndicator>
            <Tooltip content="Completed with errors">
              <AlertTriangle className="h-4 w-4 text-destructive" />
            </Tooltip>
          </div>
        )

      if (status === 'completed') {
        // A BENIGN empty page (rag status "empty": a real page with no body) is NOT a failure — show a
        // muted "Empty", never the red "No content". Without this guard an empty page (completed +
        // chunk_count 0) would fall into the destructive branch below and a stub-filled Confluence space
        // would show a column of red rows — the exact false alarm this whole change removes.
        if (!isSource && row.original.is_empty) {
          return (
            <Tooltip content="This page has no content to index.">
              <StatusIndicator tone="neutral" className="cursor-help">
                Empty
              </StatusIndicator>
            </Tooltip>
          )
        }
        // A leaf doc that "completed" but embedded ZERO chunks extracted nothing usable (empty/
        // parse-failed file, a page that yielded no text, an OCR that returned nothing). Showing
        // "Active" here is a lie — surface it as a failure with the real reason on hover. Guarded
        // to leaf docs (chunk_count is only populated there); sources keep their rollup status.
        if (!isSource && row.original.chunk_count === 0) {
          return (
            <Tooltip
              content={row.original.error_detail || 'No content was extracted from this document.'}
            >
              <span className="inline-flex items-center gap-1 cursor-help">
                <StatusIndicator tone="danger">No content</StatusIndicator>
                <AlertTriangle className="h-4 w-4 text-destructive" />
              </span>
            </Tooltip>
          )
        }
        // A completed source may still carry a non-fatal note (e.g. "rows were grouped to fit the
        // chunk limit") — surface it as an amber warning beside Active, not a red failure.
        const warn = row.original.error_detail
        if (!warn) return <StatusIndicator tone="success">Active</StatusIndicator>
        return (
          <div className="flex items-center gap-2">
            <StatusIndicator tone="success">Active</StatusIndicator>
            <Tooltip content={warn}>
              <AlertTriangle className="h-4 w-4 text-status-warning cursor-help" />
            </Tooltip>
          </div>
        )
      }

      const getStatusVariant = () => {
        switch (status) {
          case 'queued':
            return 'warning'
          case 'syncing':
            return 'progress'
          case 'failed':
            return 'destructive'
          default:
            return 'default'
        }
      }
      const variant = getStatusVariant()
      const badge = (
        <Badge
          variant={variant}
          className={cn('capitalize gap-1', variant !== 'destructive' && ' bg-transparent px-0')}
        >
          {variant !== 'destructive' && (
            <RefreshCcw size={16} className={`text-${variant}-foreground`} />
          )}
          {status}
        </Badge>
      )
      // Surface the real failure cause on hover (e.g. "file has too many sections", an OCR error)
      // so the user sees WHY it failed instead of just a red "failed" badge.
      if (variant === 'destructive' && row.original.error_detail) {
        // Strip the internal "pipeline ingest failed: err:" plumbing prefix so the user sees the
        // actionable cause, not our wrapper jargon.
        const cleanError = row.original.error_detail
          .replace(/^pipeline ingest failed:\s*/i, '')
          .replace(/^err:\s*/i, '')
        return (
          <Tooltip content={cleanError}>
            <span className="inline-flex items-center gap-1 cursor-help">
              {badge}
              <AlertTriangle className="h-4 w-4 text-destructive" />
            </span>
          </Tooltip>
        )
      }
      return badge
    },
  },
  {
    id: 'source',
    header: 'Source',
    Cell: ({ row }) => {
      const { source, app_source: appSource, isSource } = row.original

      // Render the source kind on parent (source) rows AND on FILE leaf rows. A file renders as a flat
      // document row with no parent source row of its own, so without this its Source column goes blank
      // once it finishes ingesting (gets a Document row). url/app CHILD docs keep a blank Source on
      // purpose — their parent source row already shows it.
      if (!isSource && source !== KBDocumentSource.FILE) {
        return null
      }

      if (source === KBDocumentSource.URL) {
        return (
          <span className="flex items-center gap-2">
            <Globe size={16} className="text-primary" /> Website
          </span>
        )
      }

      if (source === KBDocumentSource.APP) {
        return (
          <span className="flex items-center gap-2">
            {getAppIcon(appSource, apps)}
            {capitalize(appSource)}
          </span>
        )
      }

      return (
        <span className="flex items-center gap-2">
          <FileText size={16} className="text-secondary" />
          {capitalize(source)}
        </span>
      )
    },
  },
  {
    id: 'updated_by',
    header: 'Updated By',
    Cell: ({ row }) => {
      const updatedBy = row.original.updated_by?.name
      return <div className="capitalize">{updatedBy || '—'}</div>
    },
  },
  {
    id: 'updated_at',
    header: 'Updated At',
    Cell: ({ row }) => (
      <span className="font-jetbrains text-xs-400 tabular-nums">
        {formatDateTime(row.original.updated_at, { format: DATE_TIME_FORMAT })}
      </span>
    ),
  },
  {
    id: 'actions',
    header: '',
    Cell: ({ row }) => <KBActions row={row.original} />,
    size: 48,
    enableSorting: false,
    enableHiding: false,
  },
]

export const filterFields: DataTableFilterField<RKnowledgeBase['documents'][0]>[] = [
  {
    label: 'Name',
    value: 'name',
    paramKey: 'name',
    placeholder: 'Search by name...',
  },
]
